import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { LockOutlined, PhoneAndroid, Key } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { verifyMFALogin } from '@/store/thunks/authThunks';
import {
  selectMfaUserId,
  selectMfaMethod,
  selectMfaPhoneHint,
  selectIsLoading,
} from '@/store/slices/authSlice';
import { mfaAPI } from '@/services/mfaAPI';

export const MFAVerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectMfaUserId);
  const mfaMethod = useAppSelector(selectMfaMethod);
  const phoneHint = useAppSelector(selectMfaPhoneHint);
  const isLoading = useAppSelector(selectIsLoading);

  const [code, setCode] = React.useState(['', '', '', '', '', '']);
  const [error, setError] = React.useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!userId) {
      navigate('/auth/login', { replace: true });
    }
  }, [userId, navigate]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleSubmit(fullCode);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setCode(newCode);
      if (pasted.length === 6) {
        handleSubmit(pasted);
      } else {
        inputRefs.current[pasted.length]?.focus();
      }
    }
  };

  const handleSubmit = async (fullCode?: string) => {
    const codeStr = fullCode || code.join('');
    if (codeStr.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (!userId) return;

    try {
      const response = await dispatch(verifyMFALogin(codeStr, userId, mfaMethod || 'totp'));

      const userRole = response?.user?.role?.toLowerCase();
      const allowedRoles = ['admin', 'super_admin', 'manager', 'staff', 'support',
        'operations_manager', 'operations_agent', 'support_lead', 'support_agent',
        'finance_manager', 'finance_analyst', 'legal_counsel', 'compliance_officer',
        'regional_manager', 'fleet_coordinator', 'auditor', 'developer'];

      if (userRole && !allowedRoles.includes(userRole)) {
        setError('Access denied. This dashboard is restricted to administrators only.');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        return;
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResendSms = async () => {
    if (!userId) return;
    try {
      await mfaAPI.resendSMS(userId);
    } catch {
      // silently fail
    }
  };

  if (!userId) return null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'primary.light',
            mb: 2,
          }}
        >
          <LockOutlined sx={{ fontSize: 28, color: 'primary.main' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Two-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {mfaMethod === 'sms' ? (
            <>
              <PhoneAndroid sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              We sent a code to your phone{phoneHint ? ` ending in ${phoneHint}` : ''}
            </>
          ) : (
            <>
              <Key sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              Enter the 6-digit code from your authenticator app
            </>
          )}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Code Input */}
      <Box
        sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}
        onPaste={handlePaste}
      >
        {code.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => { inputRefs.current[index] = el; }}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent)}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: 600,
                padding: '12px 0',
              },
              inputMode: 'numeric',
            }}
            sx={{
              width: 48,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
            disabled={isLoading}
            autoFocus={index === 0}
          />
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={() => handleSubmit()}
        disabled={isLoading || code.join('').length !== 6}
        sx={{ mb: 2 }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
      </Button>

      {/* Resend for SMS */}
      {mfaMethod === 'sms' && (
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Link
            onClick={handleResendSms}
            variant="body2"
            sx={{ cursor: 'pointer' }}
          >
            Resend code
          </Link>
        </Box>
      )}

      <Box sx={{ textAlign: 'center' }}>
        <Link
          onClick={() => navigate('/auth/login')}
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          Back to login
        </Link>
      </Box>
    </Box>
  );
};
